import isPlainObject from 'lodash/isPlainObject';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import isEmpty from 'lodash/isEmpty';

import { formatOptions } from '../libs/helpers';

import {
    PURCHASE_METHOD_CASH,
    PURCHASE_METHOD_LEASE,
    PURCHASE_METHOD_FINANCE,
    TRANSACTION_DEPOSIT,
    TRANSACTION_TOPAY,
    TRANSACTION_TOTAL
} from '../constants/payment';

const getMoneyFormatter = state => (value) =>
    Number(value).toLocaleString(`${state.config.lang}`, {
        style: 'currency',
        currency: 'CAD',
        maximumFractionDigits: 2,
        minimumFractionDigits: 2
    }).replace('CA', '');

const getNumberFormat = state => (value) =>
    Number(value).toLocaleString(`${state.config.lang}`, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2
    }).replace('CA', '');

const getIntegerFormatter = state => (value) =>
    Number(value).toLocaleString(`${state.config.lang}`).replace('CA', '');
/**
 * @param {Object} state
 * @returns {Object}
 */
const getPaymentOptions = state => {
    const { paymentOptions } = state;

    return isPlainObject(paymentOptions) ? paymentOptions : {};
};

/**
 * @param {Object} state
 * @returns {Boolean}
 */
const getHasTaxesIncluded = state => state.hasTaxesIncluded === true;

/**
 * @param {Object} state
 * @returns {Boolean}
 */
const getItemValidation = state => {
    const { itemValidation } = state;

    return isPlainObject(itemValidation) ? itemValidation : {};
};

/**
 * @param {Object} state
 * @returns {Boolean}
 */
const getIsQuoteChanged = state => state.isQuoteChanged === true;

/**
 * @param {Object} state
 * @returns {Object}
 */
const getCashPurchaseOption = state => {
    const paymentOptions = getPaymentOptions(state);
    const { cashPurchaseOption } = paymentOptions;

    return isPlainObject(cashPurchaseOption) ? cashPurchaseOption : {};
};

/**
 * @param {Object} state
 * @param {String} category
 * @returns {Number}
 */
const getCashPurchaseTransactionAmount = (state, category) => {
    const { transaction } = getCashPurchaseOption(state);

    if (!Array.isArray(transaction)) {
        return 0;
    }

    const row = transaction.find(item => item.category === category);

    return (row && row.amount) || 0;
};

/**
 * @param {Object} state
 * @returns {Number|null}
 */
const getCashPurchaseToPay = state => getCashPurchaseTransactionAmount(state, TRANSACTION_TOPAY);

/**
 * @param {Object} state
 * @returns {Number|null}
 */
const getCashPurchaseDeposit = state => getCashPurchaseTransactionAmount(state, TRANSACTION_DEPOSIT);

/**
 * @param {Object} state
 * @returns {Number|null}
 */
const getCashPurchaseTotal = state => getCashPurchaseTransactionAmount(state, TRANSACTION_TOTAL);

/**
 * @param {Object} state
 * @returns {Number|null}
 */
const getCashBaseSellingPrice = state => {
    const { baseSellingPrice } = getCashPurchaseOption(state);

    return isNumber(baseSellingPrice) ? baseSellingPrice : null;
};

/**
 * @param {Object} state
 * @returns {String}
 */
const getSelectedTab = state => {
    return state.selectedTab;
}

/**
 * @param {Object} state
 * @returns {String}
 */
const getPurchaseMethod = state => {
    const paymentOptions = getPaymentOptions(state);

    return paymentOptions.purchaseMethod;
};

/**
 * @param {Object} state
 * @returns {Object}
 */
const getConfig = state => state.config;

/**
 * @param {Object} state
 * @returns {Array}
 */
const getFinanceOptions = state => {
    const paymentOptions = getPaymentOptions(state);
    const { financeOptions } = paymentOptions;

    return Array.isArray(financeOptions) ? financeOptions : [];
};

/**
 * @param {Object} state
 * @returns {Boolean}
 */
const getIsCashSelected = state => {
    const { purchaseMethod } = getPaymentOptions(state);

    return purchaseMethod === PURCHASE_METHOD_CASH;
};

/**
 * @param {Object} state
 * @returns {Boolean}
 */
const getIsLeaseSelected = state => {
    const { purchaseMethod } = getPaymentOptions(state);

    return purchaseMethod === PURCHASE_METHOD_LEASE;
};

/**
 * @param {Object} state
 * @returns {Boolean}
 */
const getIsFinanceSelected = state => {
    const { purchaseMethod } = getPaymentOptions(state);

    return purchaseMethod === PURCHASE_METHOD_FINANCE;
};

/**
 * @param {Object} state
 * @returns {Array}
 */
const getMetaFinancePlans = state => {
    const { meta } = state;

    return isPlainObject(meta) && Array.isArray(meta.financePlans) ? meta.financePlans : [];
};

/**
 * @param {Object} state
 * @returns {Object}
 */
const getQuote = state => {
    const { quote } = state;

    return isPlainObject(quote) ? quote : {};
};

/**
 * @param {Object} state
 * @returns {Number}
 */
const getQuoteTransaction = state => {
    const { transaction } = getQuote(state);

    return Array.isArray(transaction) ? transaction : [];
};

/**
 * @param {Object} state
 * @returns {String|null}
 */
const getQuoteId = state => {
    const { id } = getQuote(state);

    return isString(id) ? id : null;
};

/**
 * @param {Object} state
 * @returns {Number|null}
 */
const getLeadId = state => {
    const { leadId } = getQuote(state);

    return isNumber(leadId) ? leadId : null;
};

/**
 * @param {Object} state
 * @returns {Number} Can be -1 if not found
 */
const getSelectedFinanceOptionsIndex = state => {
    const financeOptions = getFinanceOptions(state);

    return financeOptions.findIndex(item => item.isSelected);
};

/**
 * @param {Object} state
 * @returns {Object}
 */
const getSelectedFinanceOptions = state => {
    const index = getSelectedFinanceOptionsIndex(state);

    if (index === -1) {
        return {};
    }

    const financeOptions = getFinanceOptions(state);

    return financeOptions[index];
};

/**
 * @param {Object} state
 * @returns {Object}
 */
const getSelectedFinancePlan = state => {
    const financeOptions = getSelectedFinanceOptions(state);

    if (!isPlainObject(financeOptions)) {
        return {};
    }

    const financePlans = getMetaFinancePlans(state);
    const { financePlan, purchaseMethod } = financeOptions;

    const foundPlan = financePlans.find(item => financePlan === item.financePlan && purchaseMethod === item.purchaseMethod);

    return isPlainObject(foundPlan) ? foundPlan : {};
};

/**
 * @param {Object} state
 * @returns {Boolean}
 */
const getTransactionOptionDetails = state => state.optionDetails;

/**
 * @param {Object} state
 * @returns {Array}
 */
const getSortedTransactionOptionDetails = state => {
    const optionDetails = getTransactionOptionDetails(state);

    const modalDetails = formatOptions(optionDetails);

    return modalDetails;
};

/**
 * @param {Object} state
 * @returns {String}
 */
const getTransactionToken = state => {
    const { transactionToken } = getPaymentOptions(state);

    return transactionToken;
};
/**
 * @param {Object} state
 * @returns {String}
 */

const getAddDeleteTransactionToken = state => {
    const paymentOptions = getPaymentOptions(state);
    const itemValidation = getItemValidation(state);

    const transactionToken = (!isEmpty(itemValidation.negociationTransaction) && itemValidation.negociationTransaction.transactionToken)
        || paymentOptions.transactionToken;
    return transactionToken;
};

/**
 * @param {Object} state
 * @returns {Boolean}
 */
const getConflictStatus = state => {
    const itemValidation = getItemValidation(state);
    const conflictStatus = itemValidation.isValid || itemValidation.validResolutionCombinations.length === 0;
    return conflictStatus;
};

/**
 * @param {Object} state
 * @returns {Object}
 */
const getShowroomQueryParams = state => {
    const {
        lang,
        location,
        organizationId,
        organizationUnitId
    } = getConfig(state);

    return {
        lang,
        location,
        orgId: organizationId,
        orgUnitId: organizationUnitId
    };
};

/**
 * @param {Object} state
 * @returns {Boolean}
 */
const getIsReadOnly = state => {
    const { isReadOnly } = state;

    return isReadOnly === true;
};

/**
 * @param {Object} state
 * @returns {Object}
 */
const getQuoteServiceQueryParams = state => {
    const {
        location,
        organizationId,
        organizationUnitId
    } = getConfig(state);

    return {
        location,
        organizationId,
        organizationUnitId
    };
};

/**
 * @param {Object} state
 * @returns {Object}
 */
const getPermissions = state => state.permissions;

/**
 * @param {Object} state
 * @returns {Boolean}
 */
const getHasCostAndProfitAccess = state => {
    const permissions = getPermissions(state);
    return permissions.hasCostAndProfitAccess;
};

/**
 * @param {Object} state
 * @returns {Boolean}
 */
const getDisplayCostAndProfit = state => state.displayCostAndProfit;

/**
 * @param {Object} state
 * @returns {Boolean}
 */
const getCanEditRebates = state => {
    const permissions = getPermissions(state);
    return permissions.canEditRebates;
};

/**
 * @param {Object} state
 * @returns {String} The profit code
 */
const getSalesNumbers = state => {
    const quote = getQuote(state);
    const { finance, cashPurchase } = quote;
    const salesNumbers = finance ? finance.salesNumbers : cashPurchase.salesNumbers;
    return salesNumbers;
};

export {
    getMoneyFormatter,
    getNumberFormat,
    getIntegerFormatter,
    getDisplayCostAndProfit,
    getCashPurchaseOption,
    getCashPurchaseToPay,
    getCashPurchaseDeposit,
    getCashPurchaseTotal,
    getCashBaseSellingPrice,
    getConfig,
    getFinanceOptions,
    getHasCostAndProfitAccess,
    getIsCashSelected,
    getIsLeaseSelected,
    getIsFinanceSelected,
    getMetaFinancePlans,
    getPaymentOptions,
    getQuoteId,
    getSelectedFinanceOptions,
    getSelectedFinancePlan,
    getHasTaxesIncluded,
    getSelectedFinanceOptionsIndex,
    getPurchaseMethod,
    getLeadId,
    getQuote,
    getQuoteTransaction,
    getIsQuoteChanged,
    getTransactionOptionDetails,
    getItemValidation,
    getSortedTransactionOptionDetails,
    getTransactionToken,
    getConflictStatus,
    getAddDeleteTransactionToken,
    getShowroomQueryParams,
    getQuoteServiceQueryParams,
    getIsReadOnly,
    getCanEditRebates,
    getPermissions,
    getSalesNumbers,
    getSelectedTab,
};
